// Partnership / Asana form.
//
// TEMPORARY TEST BUILD — the Asana personal access token below is hardcoded and
// therefore public to anyone who opens this file in the browser. It grants full
// read/write access to the whole workspace, not just this project. Move the
// create-task call behind a backend endpoint (and rotate this token) before this
// page goes live.
//
// Responsibilities:
//   1. "At least one" validation for required checkbox groups — native validation
//      has no concept of this. CSS owns the visual feedback via the flagged classes.
//   2. Submission: create the Asana task directly against the Asana REST API.
//   3. Discovery: on load, log the project's board columns and their section GIDs
//      to the console so the target column can be picked.

const ASANA_API = 'https://app.asana.com/api/1.0';
const ASANA_TOKEN = '2/1214815821133209/1216982809487996:befc47293215951235b8983347f43180';

/**
 * Reads the target workspace/project/column from the theme settings. Any of the
 * three can be blank here — `missingGids` decides whether that's fatal.
 * @param {HTMLFormElement} form
 * @returns {{ workspace: string, project: string, section: string }}
 */
function readAsanaConfig(form) {
    return {
        workspace: (form.dataset.asanaWorkspace || '').trim(),
        project: (form.dataset.asanaProject || '').trim(),
        section: (form.dataset.asanaSection || '').trim(),
    };
}

/**
 * Names of the GID settings left blank. All three are needed to create a task.
 * @param {{ workspace: string, project: string, section: string }} config
 */
function missingGids(config) {
    return Object.entries(config)
        .filter(([, value]) => !value)
        .map(([key]) => key);
}

const asanaHeaders = {
    Authorization: `Bearer ${ASANA_TOKEN}`,
    Accept: 'application/json',
};

/**
 * Calls the Asana REST API and unwraps the `data` envelope.
 * @param {string} path Path after /api/1.0, e.g. `/projects/123/sections`
 * @param {RequestInit} [init]
 */
async function asanaRequest(path, init = {}) {
    const response = await fetch(`${ASANA_API}${path}`, {
        ...init,
        headers: { ...asanaHeaders, ...(init.headers || {}) },
    });

    const body = await response.json().catch(() => null);
    if (!response.ok) {
        const detail = body?.errors?.map((e) => e.message).join('; ') || response.statusText;
        throw new Error(`Asana ${response.status}: ${detail}`);
    }

    return body?.data;
}

/**
 * Lists the project's board columns and their section GIDs, so the right one can
 * be pasted into the "Board column (section) GID" theme setting.
 * @param {{ workspace: string, project: string, section: string }} config
 */
async function logAsanaSections(config) {
    try {
        const sections = await asanaRequest(
            `/projects/${config.project}/sections?opt_fields=name,gid`
        );

        const target = sections.find((s) => s.gid === config.section);
        const destination = !config.section
            ? 'NOT SET — copy a section GID below into the theme setting'
            : `${target?.name || 'UNKNOWN COLUMN'} (${config.section})`;

        console.log(
            `[partnership-form] Section GIDs for project ${config.project}. Submissions go to: ${destination}`
        );
        console.table(sections.map(({ name, gid }) => ({ column: name, 'section GID': gid })));
    } catch (error) {
        console.error('[partnership-form] could not list the Asana board columns', error);
    }
}

const initializedAttribute = 'pfValidateInit';
const formErrorClass = 'partnership-form__form--error';
const groupInvalidClass = 'pf-checkboxes--invalid';

/**
 * Serializes a form into a plain object. Fields named `foo[]` (checkbox groups)
 * collapse into an array under `foo`; every other field is a single string.
 * @param {HTMLFormElement} form
 */
function serializeForm(form) {
    /** @type {Record<string, string | string[]>} */
    const data = {};

    for (const [rawKey, value] of new FormData(form).entries()) {
        if (typeof value !== 'string') continue;

        if (rawKey.endsWith('[]')) {
            const key = rawKey.slice(0, -2);
            const existing = data[key];
            if (Array.isArray(existing)) {
                existing.push(value);
            } else {
                data[key] = [value];
            }
        } else {
            data[rawKey] = value;
        }
    }

    return data;
}

/** @param {string} value */
function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/**
 * Builds the task description as Asana rich text — bold label, line break, value,
 * blank line between fields:
 *
 *   Name:
 *   Candice Feng
 *
 *   Email address:
 *   candice@msmorning.com
 *
 * Asana only bolds via `html_notes` — plain `notes` renders markup literally — and
 * it accepts a narrow tag set, so this sticks to <body> and <strong>, using raw
 * newlines for the breaks.
 * @param {Record<string, string | string[]>} fields
 */
function formatHtmlNotes(fields) {
    const blocks = Object.entries(fields).map(([key, value]) => {
        const text = Array.isArray(value) ? value.join(', ') : value;
        return `<strong>${escapeHtml(key)}:</strong>\n${escapeHtml(text)}`;
    });

    return `<body>${blocks.join('\n\n')}</body>`;
}

let loggedSections = false;

document.querySelectorAll('.partnership-form__form').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset[initializedAttribute] === 'true') return;
    form.dataset[initializedAttribute] = 'true';

    const config = readAsanaConfig(form);

    // Discovery log — remove once the board layout is settled. This runs before the
    // completeness check below because listing the columns is how you find the
    // section GID; gating it on a filled-in section GID would be circular.
    if (!loggedSections && config.project) {
        loggedSections = true;
        logAsanaSections(config);
    }

    // Nowhere to send the task — switch the form off rather than let a native
    // submit reload the page and lose what was typed.
    const missing = missingGids(config);
    if (missing.length) {
        console.error(
            `[partnership-form] form disabled — blank Asana GID setting(s): ${missing.join(', ')}`
        );
        form.addEventListener('submit', (event) => event.preventDefault());
        form.querySelectorAll('[type="submit"]').forEach((btn) => {
            btn.disabled = true;
        });
        return;
    }

    const requiredGroups = Array.from(form.querySelectorAll('[data-pf-required-group]'));
    const isSatisfied = (group) => Boolean(group.querySelector('input[type="checkbox"]:checked'));

    // Clear a group's error as soon as it becomes satisfied.
    for (const group of requiredGroups) {
        group.addEventListener('change', () => {
            if (!isSatisfied(group)) return;
            group.classList.remove(groupInvalidClass);
            if (!requiredGroups.some((g) => g.classList.contains(groupInvalidClass))) {
                form.classList.remove(formErrorClass);
            }
        });
    }

    const statusSuccess = form.querySelector('.partnership-form__status-success');
    const statusError = form.querySelector('.partnership-form__status-error');
    const submitBtn = form.querySelector('[type="submit"]');

    const showStatus = (kind) => {
        if (statusSuccess) statusSuccess.hidden = kind !== 'success';
        if (statusError) statusError.hidden = kind !== 'error';
    };

    form.addEventListener('submit', async (event) => {
        // 1. Required checkbox groups (native validation can't express "at least one").
        let firstInvalid = null;
        for (const group of requiredGroups) {
            if (isSatisfied(group)) {
                group.classList.remove(groupInvalidClass);
            } else {
                group.classList.add(groupInvalidClass);
                if (!firstInvalid) firstInvalid = group;
            }
        }
        if (firstInvalid) {
            event.preventDefault();
            form.classList.add(formErrorClass);
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstInvalid.querySelector('input[type="checkbox"]')?.focus();
            return;
        }

        // Native required-field validation has already passed by the time `submit`
        // fires, so anything left is safe to send.
        event.preventDefault();
        showStatus(null);

        const fields = serializeForm(form);
        const firstText = Object.values(fields).find((v) => typeof v === 'string' && v.trim());

        // "<Ticket title> — <first filled-in field>", e.g. "Partnership enquiry — Vahan".
        // The prefix is a theme setting; with it blank the title is just the field value.
        const titlePrefix = (form.dataset.ticketTitle || '').trim();
        const subject = firstText || 'New submission';

        const task = {
            workspace: config.workspace,
            projects: [config.project],
            memberships: [{ project: config.project, section: config.section }],
            name: titlePrefix ? `${titlePrefix} — ${subject}` : subject,
            html_notes: formatHtmlNotes(fields),
        };

        const originalLabel = submitBtn ? submitBtn.textContent : '';
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Sending…';
        }

        try {
            const created = await asanaRequest('/tasks?opt_fields=gid,name,permalink_url', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: task }),
            });
            console.log('[partnership-form] created Asana task', created?.permalink_url, created);

            showStatus('success');
            form.reset();
            statusSuccess?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (error) {
            console.error('[partnership-form] submission failed', error);
            showStatus('error');
        } finally {
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = originalLabel;
            }
        }
    });
});

// Fake a placeholder on date fields. Native `type="date"` inputs ignore the
// `placeholder` attribute, so these render as `type="text"` (showing the label
// as a placeholder). We swap to a real date input on focus so the browser
// datepicker appears, and back to text when the field is left empty.
const dateFieldInitAttribute = 'pfDateInit';

document.querySelectorAll('input[data-pf-date]').forEach((input) => {
    if (input.dataset[dateFieldInitAttribute] === 'true') return;
    input.dataset[dateFieldInitAttribute] = 'true';

    const toDate = () => {
        input.type = 'date';
    };
    const toText = () => {
        if (!input.value) input.type = 'text';
    };

    input.addEventListener('focus', () => {
        toDate();
        // Some browsers drop the picker when the type changes under focus; nudge it open.
        if (typeof input.showPicker === 'function') {
            try {
                input.showPicker();
            } catch (_) {
                /* showPicker can throw without user activation — safe to ignore. */
            }
        }
    });
    input.addEventListener('blur', toText);

    // Autofilled / pre-populated values should display as a date immediately.
    if (input.value) toDate();
});